/**
 * useJobQueue Hook
 * Manages job queue operations and API communication
 */

import { useState, useCallback } from 'react';
import {
  Job,
  JobListResponse,
  JobFilter,
  CreateJobPayload,
  UpdateJobPayload,
  JobApiResponse,
} from '../types/job';

interface UseJobQueueReturn {
  // State
  jobs: Job[];
  loading: boolean;
  error: string | null;
  selectedJob: Job | null;

  // Operations
  fetchJobs: (filter?: JobFilter, page?: number, pageSize?: number) => Promise<void>;
  fetchJobById: (jobId: string) => Promise<Job | null>;
  createJob: (payload: CreateJobPayload) => Promise<Job | null>;
  updateJob: (jobId: string, payload: UpdateJobPayload) => Promise<Job | null>;
  deleteJob: (jobId: string) => Promise<boolean>;
  selectJob: (job: Job | null) => void;
  clearError: () => void;

  // File operations
  uploadFile: (file: File) => Promise<string>; // Returns file ID
  deleteFile: (fileId: string) => Promise<boolean>;
}

/**
 * Hook for managing job queue operations
 * Note: API endpoints should be implemented in your backend
 * This hook provides the interface structure for frontend-backend communication
 */
export const useJobQueue = (baseUrl?: string): UseJobQueueReturn => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);

  const apiUrl = baseUrl || '/api';

  /**
   * Fetch all jobs with optional filtering
   * Expected endpoint: GET /api/jobs
   */
  const fetchJobs = useCallback(
    async (filter?: JobFilter, page = 1, pageSize = 20) => {
      try {
        setLoading(true);
        setError(null);

        const queryParams = new URLSearchParams();
        if (filter?.status?.length) {
          queryParams.append('status', filter.status.join(','));
        }
        if (filter?.category?.length) {
          queryParams.append('category', filter.category.join(','));
        }
        if (filter?.templateId) {
          queryParams.append('templateId', filter.templateId);
        }
        if (filter?.searchText) {
          queryParams.append('search', filter.searchText);
        }
        if (filter?.tags?.length) {
          queryParams.append('tags', filter.tags.join(','));
        }
        queryParams.append('page', String(page));
        queryParams.append('pageSize', String(pageSize));

        const url = `${apiUrl}/jobs?${queryParams.toString()}`;
        const response = await fetch(url);

        if (!response.ok) {
          // If API doesn't exist yet, just show empty list
          if (response.status === 404 || response.status === 500) {
            setJobs([]);
            return;
          }
          throw new Error(`Failed to fetch jobs: ${response.statusText}`);
        }

        const data: JobApiResponse<JobListResponse> = await response.json();
        if (data.success && data.data) {
          setJobs(data.data.jobs);
        } else {
          throw new Error(data.error || 'Failed to fetch jobs');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        // Don't show error for missing API - just show empty list
        if (!errorMessage.includes('Failed to fetch')) {
          setError(errorMessage);
        }
        console.error('Error fetching jobs:', err);
        setJobs([]);
      } finally {
        setLoading(false);
      }
    },
    [apiUrl]
  );

  /**
   * Fetch a single job by ID
   * Expected endpoint: GET /api/jobs/:jobId
   */
  const fetchJobById = useCallback(
    async (jobId: string): Promise<Job | null> => {
      try {
        setError(null);
        const response = await fetch(`${apiUrl}/jobs/${jobId}`);

        if (!response.ok) {
          throw new Error(`Failed to fetch job: ${response.statusText}`);
        }

        const data: JobApiResponse<Job> = await response.json();
        if (data.success && data.data) {
          return data.data;
        } else {
          throw new Error(data.error || 'Failed to fetch job');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        console.error('Error fetching job:', err);
        return null;
      }
    },
    [apiUrl]
  );

  /**
   * Create a new job
   * Expected endpoint: POST /api/jobs
   */
  const createJob = useCallback(
    async (payload: CreateJobPayload): Promise<Job | null> => {
      try {
        setError(null);
        const response = await fetch(`${apiUrl}/jobs`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`Failed to create job: ${response.statusText}`);
        }

        const data: JobApiResponse<Job> = await response.json();
        if (data.success && data.data) {
          setJobs((prev) => [data.data!, ...prev]);
          return data.data;
        } else {
          throw new Error(data.error || 'Failed to create job');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        console.error('Error creating job:', err);
        return null;
      }
    },
    [apiUrl]
  );

  /**
   * Update an existing job
   * Expected endpoint: PUT /api/jobs/:jobId
   */
  const updateJob = useCallback(
    async (jobId: string, payload: UpdateJobPayload): Promise<Job | null> => {
      try {
        setError(null);
        const response = await fetch(`${apiUrl}/jobs/${jobId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`Failed to update job: ${response.statusText}`);
        }

        const data: JobApiResponse<Job> = await response.json();
        if (data.success && data.data) {
          setJobs((prev) => prev.map((j) => (j.id === jobId ? data.data! : j)));
          if (selectedJob?.id === jobId) {
            setSelectedJob(data.data);
          }
          return data.data;
        } else {
          throw new Error(data.error || 'Failed to update job');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        console.error('Error updating job:', err);
        return null;
      }
    },
    [apiUrl, selectedJob?.id]
  );

  /**
   * Delete a job
   * Expected endpoint: DELETE /api/jobs/:jobId
   */
  const deleteJob = useCallback(
    async (jobId: string): Promise<boolean> => {
      try {
        setError(null);
        const response = await fetch(`${apiUrl}/jobs/${jobId}`, {
          method: 'DELETE',
        });

        if (!response.ok) {
          throw new Error(`Failed to delete job: ${response.statusText}`);
        }

        const data: JobApiResponse<void> = await response.json();
        if (data.success) {
          setJobs((prev) => prev.filter((j) => j.id !== jobId));
          if (selectedJob?.id === jobId) {
            setSelectedJob(null);
          }
          return true;
        } else {
          throw new Error(data.error || 'Failed to delete job');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        console.error('Error deleting job:', err);
        return false;
      }
    },
    [apiUrl, selectedJob?.id]
  );

  /**
   * Upload a file for use in a job
   * Expected endpoint: POST /api/files/upload
   */
  const uploadFile = useCallback(
    async (file: File): Promise<string> => {
      try {
        setError(null);
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${apiUrl}/files/upload`, {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`Failed to upload file: ${response.statusText}`);
        }

        const data: JobApiResponse<{ fileId: string }> = await response.json();
        if (data.success && data.data) {
          return data.data.fileId;
        } else {
          throw new Error(data.error || 'Failed to upload file');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        console.error('Error uploading file:', err);
        throw err;
      }
    },
    [apiUrl]
  );

  /**
   * Delete an uploaded file
   * Expected endpoint: DELETE /api/files/:fileId
   */
  const deleteFile = useCallback(
    async (fileId: string): Promise<boolean> => {
      try {
        setError(null);
        const response = await fetch(`${apiUrl}/files/${fileId}`, {
          method: 'DELETE',
        });

        if (!response.ok) {
          throw new Error(`Failed to delete file: ${response.statusText}`);
        }

        const data: JobApiResponse<void> = await response.json();
        return data.success;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        console.error('Error deleting file:', err);
        return false;
      }
    },
    [apiUrl]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    jobs,
    loading,
    error,
    selectedJob,
    fetchJobs,
    fetchJobById,
    createJob,
    updateJob,
    deleteJob,
    selectJob: setSelectedJob,
    clearError,
    uploadFile,
    deleteFile,
  };
};
